import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import type { App } from 'vue'
import { toast } from 'vue3-toastify'

import { getLocale } from '@/boot/plugins/i18n'
import { laravelEcho } from '@/boot/providers/echo'
import { getAuthCookie, removeAuthCookie } from '@/utils/auth-cookie'
import { failureMessage } from '@/utils/form-errors'
import { isLoggingOut, withLogoutSignal } from '@/utils/logout-signal'
import http from '@/utils/common-http'

/**
 * Dispatched on `window` when EMR rejects the token. Keeping it an event means
 * the transport needs no router import, so there is no import cycle between the
 * HTTP layer and routing.
 */
export const UNAUTHENTICATED_EVENT = 'auth:unauthenticated'
export const TERMS_REQUIRED_EVENT = 'privacy:terms-required'

declare module 'vue' {
  interface ComponentCustomProperties {
    $http: AxiosInstance
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * The caller renders this request's failure inline, so the interceptor
     * must not toast it too. The session-expiry path still runs.
     */
    silent?: boolean
  }
}

export type ApiErrorKind =
  | 'cancelled'
  | 'network'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'conflict'
  /** A state check EMR refused, carrying its own message for the form that caused it. */
  | 'bad_request'
  | 'server'
  | 'unknown'

/** An axios error after the response interceptor has classified it. */
export interface ClassifiedAxiosError extends AxiosError {
  kind: ApiErrorKind
}

/**
 * Pure classifier: turns an axios failure into the outcome callers actually
 * branch on, so no consumer has to read status codes or axios error codes.
 */
export function classifyApiError(error: AxiosError): ApiErrorKind {
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
    return 'cancelled'
  }

  const status = error.response?.status

  if (status === undefined) return 'network'
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 422) return 'validation'
  if (status === 409) return 'conflict'
  if (status === 400) return 'bad_request'
  if (status >= 500) return 'server'

  return 'unknown'
}

export function attachRequestContext(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const token = getAuthCookie()
  if (token) config.headers.Authorization = `Bearer ${token}`

  config.headers['X-Locale'] = getLocale()

  // Lets Laravel's toOthers() skip the tab that caused the change.
  const socketId = laravelEcho?.socketId()
  if (socketId) config.headers['X-Socket-ID'] = socketId

  // `/logout` opts out: it is the request that revokes the token, so it must
  // survive the abort that logging out triggers.
  if (config.url?.endsWith('/logout') !== true) {
    // axios types `signal` as GenericAbortSignal — a structural subset that
    // `AbortSignal.any` cannot consume. A caller passing one of those keeps it
    // unchanged rather than losing their own cancellation to a cast.
    const existing = config.signal
    config.signal =
      existing === undefined || existing instanceof AbortSignal
        ? withLogoutSignal(existing)
        : existing
  }

  return config
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

/**
 * Endpoints that take no session, so their 401 is a verdict on the credentials
 * just supplied rather than a session that died. Running the expiry path here
 * signed the user out mid-sign-in and toasted "Your session has expired" at
 * someone who had merely mistyped their password.
 */
const SIGN_IN_PATHS = ['/login', '/google2fa', '/reset-password', '/reset-password-link']

function isSignInAttempt(error: AxiosError): boolean {
  const url = error.config?.url
  return url !== undefined && SIGN_IN_PATHS.some((path) => url.endsWith(path))
}

export function handleError(error: AxiosError): Promise<never> {
  const kind = classifyApiError(error)
  ;(error as ClassifiedAxiosError).kind = kind

  if (kind === 'unauthenticated' && !isSignInAttempt(error)) {
    removeAuthCookie()

    // A logout already under way produces an expected burst of 401s from the
    // requests it just aborted. Re-dispatching would race the redirect.
    if (!isLoggingOut()) {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT))
    }
  }

  const data = error.response?.data as { code?: unknown } | undefined
  if (kind === 'forbidden' && data?.code === 'terms_acceptance_required') {
    window.dispatchEvent(
      new CustomEvent(TERMS_REQUIRED_EVENT, { detail: { method: error.config?.method ?? 'get' } }),
    )
  }

  // 'validation' is rendered by the form that caused it, 'cancelled' is this app
  // aborting its own request, and a `silent` request is rendered inline by its
  // caller — none of those may toast.
  const toasts =
    kind === 'server' ||
    kind === 'network' ||
    kind === 'unknown' ||
    kind === 'not_found' ||
    // Still toasts by default: only a caller that renders it, like the recovery dialog, opts out
    // with `silent`, so a 400 nobody handles is not swallowed.
    kind === 'bad_request'
  if (toasts && error.config?.silent !== true) {
    toast.error(failureMessage(error.response?.data) || error.message || GENERIC_ERROR)
  }

  return Promise.reject(error)
}

// Registered at module scope rather than inside `install`, so a direct
// `import { http }` behaves identically whether or not the plugin was
// installed — unit tests and services get the same client the app uses.
http.interceptors.request.use(attachRequestContext)
http.interceptors.response.use((response) => response, handleError)

export default {
  install: (app: App): void => {
    app.config.globalProperties.$http = http
    app.provide('$http', http)
  },
}

export { http }
